import {MessageOutlined, MessageFilled} from '@ant-design/icons';
import {Popover} from 'antd';
import Form from 'antd/es/form';
import {useForm} from 'antd/es/form/Form';
import Input from 'antd/es/input';
import cn from 'classnames';
import React, {useState, useEffect, FC} from 'react';

import {AIS_ROUTES} from 'components/ais/ais.constants';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {showMessage} from 'shared/utils/notifications';
import {showRequestErrorMessage} from 'shared/utils/show-message-from-response';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {fetchForecastComments, updateForecastComments} from '../../api/thunks';
import {selectForecastCommentsFilters} from '../../model/slice';
import {TForecastCommentsCore, IForecastCommentsEditModal} from '../../model/types';
import styles from './edit-modal.module.less';
import {createForecastSubmitData} from './edit-modal.utils';
import {MAX_MESSAGE_LENGTH} from './edit.modal.constants';
import {useForecastCommentsFormFields} from './hooks/use-forecast-comments-form-fields';
import {useRequiredFieldsFilled} from './hooks/use-required-fields-filled';

export const ForecastCommentsEditModal: FC<IForecastCommentsEditModal> = ({
    forecastComments,
    value,
}: IForecastCommentsEditModal) => {
    const [form] = useForm<TForecastCommentsCore>();

    const [isOpen, setIsOpen] = useState(false);

    const dispatch = useAppDispatch();

    const {requestId, status} = useAppSelector(selectForecastCommentsFilters) ?? {};

    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [AIS_ROUTES.FORECAST_COMMENTS]});

    const isNewRequest = status === false;
    const formSections = useForecastCommentsFormFields(forecastComments.factors, isNewRequest);
    const isEditDisabled = !(isNewRequest && hasUserRoleFunction(EUserRoleFunction.UPDATE));

    const isRequiredFieldsFilled = useRequiredFieldsFilled(forecastComments);

    useEffect(() => {
        if (isOpen && forecastComments.factors) {
            const initialValues = {
                factors: forecastComments.factors.map((factor) => ({
                    forecastValueFts: factor.forecastValueFts,
                    forecastCommentFts: factor.forecastCommentFts,
                    forecastValueOrg: factor.forecastValueOrg,
                    forecastCommentOrg: factor.forecastCommentOrg,
                })),
            };

            form.setFieldsValue(initialValues);
        }
    }, [isOpen]);

    const handleFinish = async (factors: Pick<TForecastCommentsCore, 'factors'>) => {
        if (!requestId) {
            console.error('Ошибка: requestId не найден');
            return;
        }

        const submitData = createForecastSubmitData(forecastComments, factors);

        try {
            const data = await dispatch(updateForecastComments(submitData)).unwrap();

            showMessage({message: data});
            dispatch(fetchForecastComments({requestId}));
            setIsOpen(false);
        } catch (error) {
            showRequestErrorMessage(error);
        }
    };

    const handleAfterModalClose = () => {
        form.resetFields();
    };

    const handleModalSave = () => {
        form.submit();
    };

    return (
        <div className={styles.container}>
            <ModalOpenerComponent
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                component={
                    <Popover
                        placement="bottomRight"
                        mouseEnterDelay={1}
                        content="Прогнозные значения и комментарии"
                    >
                        <div
                            className={cn(
                                styles['icon-container'],
                                isRequiredFieldsFilled && styles['icon-container-active']
                            )}
                        >
                            {isRequiredFieldsFilled ? <MessageFilled /> : <MessageOutlined />}
                        </div>
                    </Popover>
                }
                modalProps={{title: 'Прогнозные значения и комментарии', destroyOnClose: true, centered: true}}
                handleSave={handleModalSave}
                hideControls={{save: isEditDisabled}}
                afterModalClose={handleAfterModalClose}
                controlLabels={{save: 'Сохранить', cancel: 'Отмена'}}
            >
                <Form
                    className={styles.form}
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    {formSections.map((section) => (
                        <div
                            className={styles['form-fields']}
                            key={section.sectionTitle}
                        >
                            <div className={styles['form__section-title']}>{section.sectionTitle}</div>

                            {section.fields.map((field) => (
                                <Form.Item
                                    key={field.key}
                                    name={field.name}
                                    label={field.label}
                                    rules={field.rules}
                                >
                                    {field.component === 'textarea' ? (
                                        <Input.TextArea
                                            className={styles['text-area']}
                                            placeholder={field.placeholder}
                                            disabled={field.disabled}
                                            rows={3}
                                            maxLength={MAX_MESSAGE_LENGTH}
                                            showCount
                                        />
                                    ) : (
                                        <Input
                                            placeholder={field.placeholder}
                                            disabled={field.disabled}
                                        />
                                    )}
                                </Form.Item>
                            ))}
                        </div>
                    ))}
                </Form>
            </ModalOpenerComponent>
            {value}
        </div>
    );
};

ForecastCommentsEditModal.displayName = 'ForecastCommentsEditModal';
